import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  AssociatedTestTemplateMutationUpdateArgs,
  AssociatedTestTemplateUpdateFailure,
  AssociatedTestTemplateUpdateInput,
  UpdateAssociatedTestTemplateMutation,
} from "@/graphql/codegen/graphql"
import { UpdateAssociatedTestTemplateDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateAssociatedTestTemplateReturn {
  update: (input: AssociatedTestTemplateUpdateInput) => Promise<void>
  isUpdating: boolean
  error?: CombinedError
}

export interface UseUpdateAssociatedTestTemplateProps {
  errorMessage?: string
  successMessage?: string
  onSuccess?: () => void
}

export const useUpdateAssociatedTestTemplate = ({
  errorMessage = "Failed to update associated test template.",
  successMessage = "Associated test template successfully updated.",
  onSuccess,
}: UseUpdateAssociatedTestTemplateProps): UseUpdateAssociatedTestTemplateReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    UpdateAssociatedTestTemplateMutation,
    AssociatedTestTemplateMutationUpdateArgs
  >(UpdateAssociatedTestTemplateDocument)

  const update = async (input: AssociatedTestTemplateUpdateInput) => {
    const { data, error } = await executeMutation({ input })
    const output = data?.associatedTestTemplate.update
    const hasError = error || output?.__typename === "AssociatedTestTemplateUpdateFailure"

    if (hasError) {
      notify({
        type: NotificationType.Error,
        message: error?.message ?? (output as AssociatedTestTemplateUpdateFailure).error.message ?? errorMessage,
      })
      return
    }

    notify({
      type: NotificationType.Success,
      message: successMessage,
    })
    onSuccess?.()
  }

  return {
    update,
    isUpdating: fetching,
    error,
  }
}
